<template>
  <div>
    <vx-card class="mb-5" title="Learning and Resources">
      <h4 class="mb-5">SWOT / Situational Awareness</h4>
      <p class="mb-5">
        A SWOT analysis is a tried-and-tested tool of strategic analysis. It’s a way of creating a
        snapshot of how your business is going, think about how your organisation could be affected
        by external factors in the future, and think about what could be done to handle or mitigate
        these factors.
      </p>
      <p class="mb-5">
        SWOTs are usually used in a business context, but can be applied to virtually any goal
        you’re looking to achieve. They can be done for a product, a service, an employee or the
        business overall.
      </p>
      <centered-image-wrapper
        :image="image1"
        classes="2/3"
        alt="SWOT analysis grid. Columns: Helpful to achieving the objective, and Harmful to achieving the objective. Rows: Internal origin (attributes of the organisation), and External origin (attributes of the environment). Internal and helpful: Strengths. Internal and harmful: Weaknesses. External and helpful: Opportunities. External and harmful: Threats."
      />
      <p class="mb-5">
        Source: Wikimedia Commons (https://commons.wikimedia.org/wiki/File:SWOT_en.svg)
      </p>
      <p class="mb-5">
        Let’s take a look at what facts and ideas belong in each quadrant, using the example of a
        local small burger restaurant.
      </p>

      <h5 class="mb-5">Strengths</h5>
      <p class="mb-5">
        Strengths are the attributes of your product, service or business that contribute positively
        towards its objective. These ones are based on what is going on inside the organisation -
        things that are within your ability to change or alter.
      </p>
      <p class="mb-5">In our example of our burger restaurant, relevant points could include:</p>

      <ul class="default-list">
        <li>Our burgers are well-reviewed in local newspapers and on social media</li>
        <li>Our burgers are higher quality than other chain fast food restaurants in the area</li>
        <li>We are currently running at a healthy profit</li>
      </ul>

      <h5 class="mb-5">Weaknesses</h5>
      <p class="mb-5">
        Weaknesses are attributes that are standing in the way of you or your business achieving
        your goals. Much like strengths, these are based on what is happening inside your business
        or aspects of your product.
      </p>
      <p class="mb-5">Examples at the burger restaurant could include:</p>

      <ul class="default-list">
        <li>Our menu is quite limited - we currently only serve beef and chicken burgers</li>
        <li>
          We have a small delivery radius, meaning that less people are able to order our food
          delivered to their doors
        </li>
      </ul>

      <h5 class="mb-5">Opportunities</h5>
      <p class="mb-5">
        Opportunities are things that are happening in the surrounding business environment that
        could be helpful for you to achieve your goals. These aren’t things that have to do with you
        or your business; they are purely external.
      </p>
      <p class="mb-5">
        For the burger shop, this could be something like a train station opening nearby that will
        increase foot traffic, or you’ve discovered that there’s a gap in the market for a good
        veggie burger.
      </p>

      <h5 class="mb-5">Threats</h5>
      <p class="mb-5">
        Threats are things that are standing in the way of your goals, but aren’t things you can
        control within your business. Classic threats include market competitors, economic downturns
        or increasing rents.
      </p>
      <p class="mb-5">
        In our example, a threat might be a chain fast food restaurant opening in the area, or that
        people are eating less fast food year-by-year than they used to.
      </p>
      <p class="mb-5">
        You can use the information based on these SWOTs to help form a strategy where you
        <b
          >build your strengths, minimize weaknesses, take advantage of opportunities and avoid
          threats</b
        >.
      </p>
    </vx-card>
    <vx-card>
      <h4 class="mb-5">How to do Research and Development</h4>
      <p class="mb-5">
        According to
        <a
          href="https://s3-us-west-2.amazonaws.com/cbi-content/research-reports/The-20-Reasons-Startups-Fail.pdf"
          target="_blank"
          >CB Insights</a
        >, the most common reason that start-ups fail is because they failed to figure out if the
        market needed their product. Let’s take a look at what you can do early on to increase your
        odds of finding a product that is a good fit for the market.
      </p>
      <p class="mb-5">
        Firstly, there are three core questions to address when building your business idea:
      </p>
      <centered-image-wrapper
        :image="image2"
        classes="2/3"
        alt="Venn diagram of three overlapping circles: Feasible, Desirable and Viable. Feasible asks 'Can we do this?': the risk is that a business can't manage, scale or get access to key resources (technology, IP, brand, etc.), key activities or key partners. Desirable asks 'Do they want this?': the risk is that the market a business is targeting is too small, that too few customers want the value proposition, or that the company can't reach, acquire and retain targeted customers. Viable asks 'Should we do this?': the risk is that a business cannot generate more revenue than costs (revenue stream and cost structure)."
      />

      <p class="mb-5">
        In order to answer these questions, you will need to do research and conduct experiments to
        gather information.
      </p>
      <centered-image-wrapper
        :image="image3"
        classes="2/3"
        alt="Diagram of the journey from Idea to Business. A tangled scribble straightens into a line over time while a falling arrow shows uncertainty and risk decreasing: the entrepreneur's and innovator's number one task is to reduce risk and uncertainty. The journey has two phases. First, Search and Testing, made up of Discovery (discover if your general direction is right, test basic assumptions, get first insights to course correct rapidly) and Validation (validate the direction you've taken and confirm with strong evidence that your business idea is very likely to work). Second, Execution."
      />

      <p class="mb-5">
        When you start out, it’s unlikely that you will have all the answers you need to be
        successful, so you will want to test out the assumptions (ideas without evidence) you have
        made about your business, and adjust depending on if the evidence confirms or goes against
        your predictions.
      </p>
      <p class="mb-5">
        Over time, you will find that you need to ask less ‘big questions’ that require big changes,
        and can instead adjust small parts of your business strategy to fine-tune.
      </p>
      <p class="mb-5">Some tips for good experiments:</p>
      <h4 class="mb-5">Go cheap and fast early</h4>
      <p class="mb-5">
        When you’re starting out, you won’t have a lot of money to spare, and you won’t have a lot
        of information to build upon or refer to. You’re better off in the early stages doing cheap
        research to answer simple questions to help set you off in the right direction.
      </p>
      <h4 class="mb-5">
        Increase the strength of evidence by doing different experiments for the same hypothesis
      </h4>
      <p class="mb-5">
        Having two approaches that create the same result helps you double check that your data
        reflects the reality of the situation, and not just the way you designed the experiment.
      </p>
      <p class="mb-5">
        For example, let’s imagine you’re trying to find out your city’s favourite radio station. In
        your first experiment you call people on their landlines, and the majority of people say
        their favourite station is ABC Radio National.
      </p>
      <p class="mb-5">
        However, you then run a second experiment by calling people’s mobile numbers, and this time
        the majority of people say their favourite radio station is Triple J.
      </p>
      <p class="mb-5">
        Because a lot of young people don’t own landline phones, your initial experiment method
        meant that the young people were less likely to be able to weigh in, which changed the
        outcome of your results.
      </p>
      <h4 class="mb-5">
        Pick the experiment that produces the strongest evidence, given our constraints
      </h4>
      <p class="mb-5">
        Try and think of the best questions or experiments you can produce in the least amount of
        time and for as little cost as possible. Free programs like SurveyMonkey or Google Forms can
        be very useful because they’re free, quick to make and give you real-time results and
        feedback
      </p>
      <h4 class="mb-5">Reduce uncertainty as much as possible before building</h4>
      <p class="mb-5">
        Creating a product takes a lot of time and money. The best approach is to get as much
        quality information as you can before you start building your final product.
      </p>
      <centered-image-wrapper
        :image="image4"
        classes="2/3"
        alt="Flowchart in three stages: Find the right market, Build the right thing, Build the thing right. Market research leads to the question 'Attractive market?': if no, kill; if yes, produce a market assessment (MA) and move to problem research. Problem research leads to 'Real need?': if no, kill or pivot; if yes, produce a market requirements document (MRD) and move to solution research. Solution research leads to 'Validated solution?': if no, kill or pivot; if yes, produce a product requirements document (PRD) and move to design and engineering."
      />
    </vx-card>
  </div>
</template>

<script>
import store from '@/store/store';

export default {
  data() {
    return {
      image1: require('@/assets/images/experiences/entrepreneur/lr1.png'),
      image2: require('@/assets/images/experiences/entrepreneur/lr2.png'),
      image3: require('@/assets/images/experiences/entrepreneur/lr3.png'),
      image4: require('@/assets/images/experiences/entrepreneur/lr4.png'),
    };
  },
  beforeMount() {
    store.commit('authStudent/setActiveExperienceID', this.$route.params.id);
  },
};
</script>
